import { nextTick } from 'vue'

import { formatDate } from './formatter'
import { localeDateFormat } from '@/utils/locale'

import type { LocaleDateMode } from '@/utils/locale'

/**
 * Returns today's date in `yyyy-mm-dd` format.
 */
export function getDateToday (): string {
  const today = new Date()
  const [y, m, d] = [today.getFullYear(), today.getMonth() + 1, today.getDate()].map((v) => v.toString()) as [string, string, string]
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

/**
 * Returns the number of days between 2 dates.
 */
export function daysBetween (startDate: string, endDate: string, inclusive = false): number {
  const ONE_DAY = 1000 * 60 * 60 * 24
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  return Math.round((end - start) / ONE_DAY) + (inclusive ? 1 : 0)
}

/**
 * Returns true if a valid date string.
 */
export function isValidDateString (date?: unknown): date is string {
  if (typeof date !== 'string' || !date) return false
  const currDate = new Date(`${date}T12:00:00Z`)
  return !(isNaN(currDate.getTime()))
}

/**
 * Offsets the provided date based on the provided offset params.
 * Negative offsets are supported.
 *
 * @returns If `date` is invalid then `null`, otherwise a string of the offset date formatted as `yyyy-mm-dd`
 */
export function offsetDate (date: string | null | undefined, offset: { years?: number, days?: number }): string | null {
  if (!date) return null
  // set to midday to avoid timezone offset problems
  const currDate = new Date(`${date}T12:00:00Z`)
  if (isNaN(currDate.getTime())) return null
  currDate.setUTCFullYear(currDate.getUTCFullYear() + (offset.years || 0))
  currDate.setUTCDate(currDate.getUTCDate() + (offset.days || 0))
  return currDate.toISOString().split('T')[0]!
}

/**
 * Returns date format information if the provided date matches the expected format, controlled by `mode`.
 * Also validates that the year/month/day are valid.
 * Missing segments are replaced with 1st, for example a month only picker will return the first day of the month.
 *
 * Returns false if an invalid date is provided.
 *
 * @example
 * // in en-US format
 * '01/30/2021' -> { year: 2021, month: 1, day: 30, iso: '2021-01-30' }
 * '02/30/2021' -> false
 * // with 'MM/YYYY' format
 * '02/2021' -> { year: 2021, month: 2, day: 1, iso: '2021-02-01' }
 */
export function getDateFormat (
  date: string | null | undefined,
  mode: LocaleDateMode,
): false | { year: number, month: number, day: number, iso: string } {
  if (!date) return false
  const expectedFormat = localeDateFormat(mode)
  let y = -1
  let m = -1
  let d = expectedFormat.includes('D') ? -1 : 1
  if (date.length !== expectedFormat.length) return false
  for (let i = 0; i < expectedFormat.length;) {
    const expectedChar = expectedFormat.charAt(i)
    switch (expectedChar) {
      case 'M':
      case 'D': {
        const next2 = date.slice(i, i + 2)
        if (!/\d\d/.test(next2)) return false
        if (expectedChar === 'M') m = +next2
        else d = +next2
        i += 2
        break
      }
      case 'Y': {
        const next4 = date.slice(i, i + 4)
        if (!/\d\d\d\d/.test(next4)) return false
        y = +next4
        i += 4
        break
      }
      default:
        i++
    }
  }
  /* istanbul ignore if: cannot find a logical way this would happen after above checks */
  if (y === -1 || m === -1 || d === -1) return false
  const testDate = new Date()
  testDate.setUTCFullYear(y, m - 1, d)
  if (testDate.getUTCFullYear() !== y) return false
  if (testDate.getUTCMonth() + 1 !== m) return false
  /* istanbul ignore if: cannot find a logical way this would be false if the above 2 pass */
  if (testDate.getUTCDate() !== d) return false
  return {
    year: y,
    month: m,
    day: d,
    iso: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
  }
}

/**
 * Returns the number of full years elapsed since a date (a person's age).
 */
export function ageFromToday (date: string): number {
  const [y0, m0, d0] = date.split('-') as [string, string, string]
  const birthDate = new Date(Date.UTC(+y0, +m0, +d0))
  const [y1, m1, d1] = getDateToday().split('-') as [string, string, string]
  const basisDate = new Date(Date.UTC(+y1, +m1, +d1))
  let age = basisDate.getFullYear() - birthDate.getFullYear()
  const month = basisDate.getMonth() - birthDate.getMonth()
  if (month < 0 || (month === 0 && basisDate.getDate() < birthDate.getDate())) age--
  return age
}

/**
 * Returns the time elapsed since the provided date in human readable format.
 *
 * @example
 * timeSince('date-of-2-seconds-ago') // 2 seconds
 */
export function timeSince (date: Date | string | number): string {
  if (!(date instanceof Date)) date = new Date(date)
  const seconds = Math.floor((new Date().valueOf() - date.valueOf()) / 1000)
  let interval = Math.floor(seconds / 31536000)

  if (interval >= 1) {
    return `${interval} year${interval !== 1 ? 's' : ''}`
  }
  interval = Math.floor(seconds / 2592000)
  if (interval >= 1) {
    return `${interval} month${interval !== 1 ? 's' : ''}`
  }
  interval = Math.floor(seconds / 86400)
  if (interval >= 1) {
    return `${interval} day${interval !== 1 ? 's' : ''}`
  }
  interval = Math.floor(seconds / 3600)
  if (interval >= 1) {
    return `${interval} hour${interval !== 1 ? 's' : ''}`
  }
  interval = Math.floor(seconds / 60)
  if (interval >= 1) {
    return `${interval} minute${interval !== 1 ? 's' : ''}`
  }
  return `${Math.floor(seconds)} second${seconds !== 1 ? 's' : ''}`
}

/**
 * Determines if a date can be inferred from a string, and if so returns the formatted date.
 * Use `mode` to specify the granularity of date to check, see {@link LocaleDateMode}.
 *
 * Returns false if an unparsable date is provided.
 *
 * @example
 * // in en-US format
 * inferDate('2/2/23', LocaleDateMode.DAY) // "02/02/2023"
 * inferDate('2/2', LocaleDateMode.DAY) // false
 * inferDate('2/99/23', LocaleDateMode.DAY) // false
 * inferDate('2/23', LocaleDateMode.MONTH) // "02/2023"
 */
export function inferDate (
  date: string,
  mode: LocaleDateMode,
): false | string {
  // Extract date and ignore timezone it isn't offset
  try {
    if (date.trim() === '') return false
    // Check the date against appropriate regex based on format - defaults to MM/DD/YYYY
    const localeFormat = localeDateFormat(mode)
    let formatted = ''

    switch (localeFormat) {
      case 'MM/YYYY': {
        // already in month format
        if (/^(0[1-9]|1[012])\/\d{4}$/.test(date)) return date
        const match = /^(0?[1-9]|1[012])\/(\d{2}|\d{4})$/.exec(date)
        if (match) {
          const month = match[1]!
          const year = match[2]!
          // matches the 2 digit year expansion used for full dates (e.g. 23 -> 2023)
          const fullYear = year.length === 4 ? year : new Date(`${month}/01/${year}`).getFullYear()
          formatted = `${month.padStart(2, '0')}/${fullYear}`
        }
        break
      }
      case 'DD/MM/YYYY':
        // already in en-GB format
        if (/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/.test(date)) {
          return date
        } else if (/^([0-2]?[1-9]|3[01])\/(0?[1-9]|1[012])\/(\d{2,4})$/.test(date)) {
          // convert to en-GB format
          const parts = date.split('/')
          const parsed = new Date(`${parts[1]}/${parts[0]}/${parts[2]}`)
          parsed.setTime(parsed.getTime() - parsed.getTimezoneOffset() * 60 * 1000)
          formatted = formatDate(parsed.toISOString()) ?? false
        }
        break
      case 'MM/DD/YYYY':
      default:
        // already in en-US format
        if (/^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(date)) {
          return date
        } else if (/^(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{2,4})$/.test(date)) {
          // convert en-US format
          const parsed = new Date(date)
          parsed.setTime(parsed.getTime() - parsed.getTimezoneOffset() * 60 * 1000)
          formatted = formatDate(parsed.toISOString()) ?? false
        }
    }
    return formatted || false
  } catch (err) {
    console.error(err)
  }
  return false
}

/**
 * Event handler for date input keydown events from text and textarea inputs.
 * Automatically inserts "/" between date segments and pads segments with leading zeros.
 * Also prevents invalid dates from being entered.
 * Use `mode` to specify the granularity of date to check.
 *
 * @returns Returns false if event should be ignore, otherwise returns the formatted date string
 */
export function handleDateKeydown (
  event: KeyboardEvent,
  mode: LocaleDateMode,
): false | string {
  const { key } = event

  if (!/^[\d/]|Backspace$/.test(key)) {
    event.preventDefault()
    return false
  }

  // inputs are always "/" separated, so only the segment lengths of the locale format are used
  const segmentLengths = localeDateFormat(mode).split(/[^DMY]+/).map((s) => s.length)
  const lastSegmentIndex = segmentLengths.length - 1
  const maxLength = segmentLengths.reduce((total, length) => total + length, lastSegmentIndex)
  const segmentEnds = segmentLengths.slice(0, lastSegmentIndex)
    .map((_, index) => segmentLengths.slice(0, index + 1).reduce((total, length) => total + length, index))

  const target = event.target as HTMLInputElement
  const { value } = target

  let selectionStart = target.selectionStart!
  const selectionEnd = target.selectionEnd!

  // construct input value as it will be after the key press
  let updatedValue = value.slice(0, selectionStart) + key + value.slice(selectionEnd)
  let line = (updatedValue.substring(0, selectionStart).match(/\n/g) || []).length
  let lines = updatedValue.split('\n')

  if (key === 'Backspace') {
    event.preventDefault()
    const selectionDelta = selectionEnd - selectionStart
    const deletedValue = value.slice(
      selectionDelta ? selectionStart : selectionStart - 1,
      selectionDelta ? selectionEnd : selectionStart,
    )

    // when forward slash is deleted remove its leading digit instead
    if (deletedValue === '/') {
      updatedValue = value.slice(0, selectionStart - 2) + value.slice(selectionStart - 1)
      selectionStart -= 2
    } else if (selectionDelta < 2 && deletedValue !== '') {
      updatedValue = value.slice(0, selectionStart - 1) + value.slice(selectionStart)
      selectionStart -= 1
    } else {
      updatedValue = value.slice(0, selectionStart) + value.slice(selectionEnd)
    }
    lines = updatedValue.split('\n')
    line = lines.length - 1 < line ? lines.length - 1 : line
    updatedValue = lines[line]!
  } else {
    updatedValue = lines[line]!
    if (key === '/') {
      const segments = updatedValue.split('/').filter((s) => !!s)
      selectionStart += 1

      // forward slash manually inserted,
      // see if current segment needs to be padded
      if (segments.length > lastSegmentIndex || !segments.length) {
        event.preventDefault()
        return false
      }
      segments.forEach((segment, index) => {
        if (segment.length !== 1) return
        segments[index] = `0${segment}`
        selectionStart += 1
      })

      updatedValue = segments.join('/')
    } else {
      const lineSelectionStart = selectionStart - value.substring(0, selectionStart).lastIndexOf('\n')
      // when digit is entered, bump cursor position if current segment is full
      selectionStart += segmentEnds.includes(lineSelectionStart) ? 2 : 1
    }

    event.preventDefault()
    if (updatedValue.length > maxLength) return false
  }

  // wait for model to update, then adjust cursor position
  void nextTick(async () => {
    await nextTick()
    target.setSelectionRange(selectionStart, selectionStart)
  })

  // create formatted date string
  let remaining = updatedValue.replace(/\//g, '')
  const segments = segmentLengths
    .map((length, index) => {
      if (index === lastSegmentIndex) return remaining
      const segment = remaining.substring(0, length)
      remaining = remaining.substring(length)
      return segment
    })
    .filter((segment, index, array) => segment.length || (index !== 0 && array[index - 1]!.length === segmentLengths[index - 1]))
  lines[line] = segments.join('/')
  return lines.join('\n')
}
